import React, { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import Edit from '@material-ui/icons/Edit';

import PaddedPaper from './PaddedPaper';

interface IPropsEnhanced extends WithStyles<typeof styles> {
  header: (editing: boolean) => ReactNode;
  body: (editing: boolean) => ReactNode;
  toggle: (editing: boolean) => ReactNode;
  actions?: (editing: boolean) => ReactNode;
  controls?: (editing: boolean, setEditing: (editing: boolean) => void) => ReactNode;
}

const styles = (theme: Theme) =>
  createStyles({
    button: {
      marginTop: theme.spacing(4),
      minWidth: 150,
    },
  });

const Panel: FC<IPropsEnhanced> = ({ classes, actions, header, body, toggle, controls }) => {
  const [editing, setEditing] = useState(false);
  const { t } = useTranslation();

  function renderEditView() {
    return (
      <PaddedPaper>
        <Grid container alignItems="center" justify="space-between">
          <Grid item xs={10}>
            {header(editing)}
          </Grid>
          {toggle(editing)}
        </Grid>
        {body(editing)}
        {controls ? controls(editing, setEditing) : renderDefaultControls()}
      </PaddedPaper>
    );
  }

  function renderDefaultControls() {
    return (
      <Grid container justify="flex-end">
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            color="secondary"
            onClick={() => setEditing(false)}>
            {t('common:done')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  function renderReadOnlyView() {
    return (
      <PaddedPaper>
        <Grid container alignItems="center" justify="space-between">
          <Grid item xs>
            {header(editing)}
          </Grid>
          <Grid item>
            {!!actions && actions(editing)}
            <Button onClick={() => setEditing(true)} endIcon={<Edit fontSize="small" />}>
              {t('common:edit')}
            </Button>
          </Grid>
          {toggle(editing)}
        </Grid>
        {body(editing)}
      </PaddedPaper>
    );
  }

  return editing ? renderEditView() : renderReadOnlyView();
};

export default withStyles(styles)(Panel);
